import { Platform } from "@/types/profile";

export const platformDisplayNames: Record<string, string> = {
  'macos': 'macOS',
  'ios': 'iOS',
  'ipados': 'iPadOS',
  'tvos': 'tvOS',
  'watchos': 'watchOS',
  'visionos': 'visionOS',
};

/**
 * Canonical lowercase platform keys used for all internal comparisons.
 */
export type CanonicalPlatform =
  | 'macos'
  | 'ios'
  | 'ipados'
  | 'tvos'
  | 'watchos'
  | 'visionos';

const CANONICAL_PLATFORMS: ReadonlySet<CanonicalPlatform> = new Set([
  'macos',
  'ios',
  'ipados',
  'tvos',
  'watchos',
  'visionos',
]);

/**
 * Aliases that may appear in API payloads or user input. Mapped to the
 * canonical lowercase key so comparisons are deterministic regardless of
 * casing or punctuation (e.g. "Mac OS", "OSX", "iPad OS").
 */
const PLATFORM_ALIASES: Record<string, CanonicalPlatform> = {
  'mac': 'macos',
  'mac os': 'macos',
  'mac os x': 'macos',
  'macosx': 'macos',
  'osx': 'macos',
  'os x': 'macos',
  'darwin': 'macos',
  'iphone': 'ios',
  'iphoneos': 'ios',
  'ipad': 'ipados',
  'ipad os': 'ipados',
  'apple tv': 'tvos',
  'appletv': 'tvos',
  'apple watch': 'watchos',
  'applewatch': 'watchos',
  'vision pro': 'visionos',
  'visionpro': 'visionos',
};

/**
 * Normalize an arbitrary platform string into its canonical lowercase key.
 * Returns `null` when the input is empty/non-string or cannot be recognized,
 * so callers can branch deterministically without relying on falsy strings.
 */
export function normalizePlatform(value: unknown): CanonicalPlatform | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();
  if (CANONICAL_PLATFORMS.has(lower as CanonicalPlatform)) {
    return lower as CanonicalPlatform;
  }

  // Collapse internal whitespace so "Mac  OS" and "mac os" alias the same key.
  const collapsed = lower.replace(/\s+/g, ' ');
  if (PLATFORM_ALIASES[collapsed]) return PLATFORM_ALIASES[collapsed];

  const stripped = collapsed.replace(/\s+/g, '');
  if (CANONICAL_PLATFORMS.has(stripped as CanonicalPlatform)) {
    return stripped as CanonicalPlatform;
  }
  if (PLATFORM_ALIASES[stripped]) return PLATFORM_ALIASES[stripped];

  return null;
}

/**
 * Strict check that a value resolves to the given canonical platform.
 * Use this anywhere gating logic depends on the platform (e.g. macOS-only
 * features like PPPC) to avoid ad-hoc `.toLowerCase()` comparisons.
 */
export function isPlatform(value: unknown, platform: CanonicalPlatform): boolean {
  return normalizePlatform(value) === platform;
}

export const formatPlatformName = (platform: string): string => {
  const canonical = normalizePlatform(platform);
  if (canonical) return platformDisplayNames[canonical];
  return platform;
};
